import { AdaptiveFilterButtons } from '@/features/adaptiveFilterButtons/AdaptiveFilterButtons'

import { FilterButtonModel } from '@/shared/components/MobileFilterButtons/MobileFilterButtons'
import { SearchInput } from '@/shared/components/SearchInput/SearchInput'
import { useIsMobile } from '@/shared/utils'
import { referralColumns } from '../../../table'
import { syncraApi } from '@/api/main/syncraClient.ts'
import { useEffect, useState } from 'react'
import { TraderReferralsListParamsStatusEnum, TraderReferralListPayloadListResult } from 'syncra-api-client'
import { useDebounce } from '@/shared/hooks'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { ReferralMobileBlock } from '@/features/referrals/ui/refferal/mobileBlock'
import { DateRange } from 'react-day-picker'
import { useApiErrorToast } from '@/shared/hooks/useApiErrorToast'
import { ApiError } from '@/api/types'
import { getReferralStatus } from '@/entities/referral'
import { normalizeDateRangeForList } from '@/shared/lib'
import { BaseTable } from 'syncra-storybook/features'

type Props = {
  selectedIndex: number
  selectFilterBtn: (index: number) => void
  setQuery: (value: string) => void
  query: string
  dateRange: DateRange | undefined
}

const REFERRALS_TABLE_TAKE = DEFAULT_TAKE

export const RefferalsTable = ({ setQuery, query, selectFilterBtn, selectedIndex, dateRange }: Props) => {
  const isMobile = useIsMobile()
  const handleApiError = useApiErrorToast()
  const statusFilterButtons: FilterButtonModel[] = [
    { name: 'Все', label: 'all', index: TraderReferralsListParamsStatusEnum.Value1 },
    { name: 'Активные', label: 'active', index: TraderReferralsListParamsStatusEnum.Value2 },
    { name: 'Не верифицированные', label: 'unverified', index: TraderReferralsListParamsStatusEnum.Value3 },
    { name: 'Заблокированные', label: 'blocked', index: TraderReferralsListParamsStatusEnum.Value4 },
    { name: 'Удаленные', label: 'deleted', index: TraderReferralsListParamsStatusEnum.Value5 },
  ]

  const debouncedQuery = useDebounce(query, 500)
  const debouncedSelectedIndex = useDebounce(selectedIndex, 300)
  const [page, setPage] = useState<number>(0)

  const [referralsList, setReferralsList] = useState<TraderReferralListPayloadListResult | null>(null)

  // Сбрасываем страницу при изменении поискового запроса
  useEffect(() => {
    if (debouncedQuery.trim()) {
      setPage(0)
    }
  }, [debouncedQuery, debouncedSelectedIndex])

  useEffect(() => {
      setPage(0)
  }, [debouncedSelectedIndex])

  // Сбрасываем страницу при изменении даты
  useEffect(() => {
    setPage(0)
  }, [dateRange])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const take = REFERRALS_TABLE_TAKE
        const skip = page * REFERRALS_TABLE_TAKE

        const { startDate, endDate } = normalizeDateRangeForList(dateRange)

        const {data} = await syncraApi.v1.traderReferralsList({
          search: debouncedQuery.trim() || undefined,
          status: debouncedSelectedIndex !== TraderReferralsListParamsStatusEnum.Value1 ? debouncedSelectedIndex : undefined,
          startDate,
          endDate,
          take,
          skip,
      })

      setReferralsList(data?.value || null)

      } catch (error) {
        handleApiError(error as ApiError)
      }
    }

    fetchData()
  }, [debouncedQuery, page, debouncedSelectedIndex, dateRange, handleApiError])

  // Функция для обработки смены страницы
  const handlePageChange = (newSkip: number) => {
    setPage(Math.floor(newSkip / REFERRALS_TABLE_TAKE))
  }

  // Определяем общее количество элементов для пагинации
  const getTotalItems = () => {
    return referralsList?.count || 0
  }


  // Преобразуем данные API в формат для таблицы
  const allTableData = referralsList?.items?.map(item => ({
    id: item.user?.id || '',
    username: item.user?.teamName || '',
    status: getReferralStatus(item),
    dailyLimit: Number(item.todayTopupSummary?.totalFactAmountFrom) || 0,
    totalRequisitMaxDailyLimit: Number(item?.totalRequisitMaxDailyLimit) || 0,
    totalTurnover: {
      amountTo: Number(item.topupSummary?.paidFactAmountTo) || 0,
      amountFrom: Number(item.topupSummary?.totalAmountFrom) || 0,
    },
    personalProfit: Number(item.referralProfit) || 0,
    conversion: {
      percentage: item.topupSummary?.totalCount && item.topupSummary?.paidCount
        ? Math.round((item.topupSummary.paidCount / item.topupSummary.totalCount) * 100)
        : 0,
      current: item.topupSummary?.paidCount || 0,
      goal: item.topupSummary?.totalCount || 0,
    },
    refPercentage: Number(item.referralCommission?.recipientCommission) || 0,
  })) || []

  // Преобразуем данные для мобильных карточек в формат ReferralModel
  const mobileData = allTableData.map(item => ({
    id: item.id,
    username: item.username,
    status: item.status,
    dailyLimit: item.dailyLimit,
    totalRequisitMaxDailyLimit: item.totalRequisitMaxDailyLimit,
    totalTurnover: item.totalTurnover,
    personalProfit: item.personalProfit,
    conversion: item.conversion,
    refPercentage: item.refPercentage,
  }))

  return (
    <>
      <div className="flex gap-2 mb-6">
        <AdaptiveFilterButtons
          filterButtons={statusFilterButtons}
          selectedBtn={selectedIndex}
          setSelectedBtn={selectFilterBtn}
          minWidthMobile="150px"
          className="flex-1 md:mb-[40px]"
        />
        {isMobile && (
          <SearchInput onChange={(event) => setQuery(event.target.value)} className="flex-1" />
        )}
      </div>

      <BaseTable
        columns={referralColumns}
        data={allTableData}
        totalItems={getTotalItems()}
        take={REFERRALS_TABLE_TAKE}
        skip={page * REFERRALS_TABLE_TAKE}
        cardClasses="border-0"
        onPageChange={handlePageChange}
        isRowLink
        navigateTo="referrals"
        mobileCards={mobileData.map((referral) => (
          <ReferralMobileBlock key={referral.id} referral={referral} />
        ))}
      />
    </>
  )
}
